import { useState, useCallback } from 'react';

/**
 * Hook that pairs with <ConfirmDeleteDialog {...dialogProps} /> to replace
 * native window.confirm() for destructive/delete actions.
 *
 * Usage:
 *   const { confirm, dialogProps } = useConfirm();
 *   ...
 *   confirm({
 *     title: 'Delete this asset?',
 *     description: '"CR-001" will be permanently removed.',
 *     confirmLabel: 'Delete',
 *     testId: 'confirm-delete-asset',
 *     onConfirm: async () => { await assetsApi.delete(id); loadAssets(); },
 *   });
 *   ...
 *   <ConfirmDeleteDialog {...dialogProps} />
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const confirm = useCallback((opts) => setState(opts), []);

  const handleConfirm = useCallback(async () => {
    if (!state?.onConfirm) {
      setState(null);
      return;
    }
    try {
      setLoading(true);
      await state.onConfirm();
    } finally {
      setLoading(false);
      setState(null);
    }
  }, [state]);

  const dialogProps = {
    open: !!state,
    onOpenChange: (o) => { if (!o && !loading) setState(null); },
    title: state?.title,
    description: state?.description,
    footnote: state?.footnote,
    confirmLabel: state?.confirmLabel || 'Delete',
    cancelLabel: state?.cancelLabel || 'Cancel',
    loading,
    onConfirm: handleConfirm,
    testId: state?.testId || 'confirm-delete-dialog',
  };

  return { confirm, dialogProps };
}

export default useConfirm;
